import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Paperclip, Send, CheckCircle2, AlertCircle, Loader2, X } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import { FORMATIONS, CANDIDATURE_API_URL, GOOGLE_FORM_CANDIDAT, CV_MAX_SIZE, CV_ACCEPTED_TYPES } from '../data/constants';
import { OFFRES } from '../data/offres';
import { CandidatureResponse } from '../data/apiContract';
type Erreurs = Record<string, string>;

/**
 * Formulaire de candidature aux offres d'alternance.
 *
 * Deux modes, choisis automatiquement selon la configuration :
 *  - CANDIDATURE_API_URL renseignée -> formulaire natif, envoi direct du CV
 *    vers l'API PHP hébergée sur IONOS, sans ouvrir la messagerie.
 *  - CANDIDATURE_API_URL vide -> formulaire Google, qui fonctionne réellement
 *    et accepte les pièces jointes. Aucune candidature n'est perdue.
 */
export function OffreForm() {
  if (!CANDIDATURE_API_URL) {
    return <FormulaireGoogle data-id="element-554" data-mp="37uhdahij6iso" />;
  }
  return <FormulaireNatif data-id="element-555" data-mp="37viz9h134pp6" />;
}

/* -------------------------------------------------------------------------- */
/* Solution de repli active : formulaire Google fonctionnel                   */
/* -------------------------------------------------------------------------- */

function FormulaireGoogle() {
  return <div data-id="element-556" data-mp="3ll2wy8ft6wrm">
      <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark mb-3" data-id="element-557" data-mp="3llcldrl7gvv7">
        Déposer votre candidature
      </h2>
      <p className="text-muted-foreground leading-relaxed mb-8" data-id="element-558" data-mp="3vk241mkxekci">
        Renseignez vos coordonnées, la formation souhaitée et joignez votre CV.
        Notre équipe vous contacte pour échanger sur votre projet et vous
        orienter vers l'offre la plus adaptée.
      </p>
      <div className="bg-white p-4 md:p-6 rounded-3xl shadow-xl border border-slate-100" data-id="element-559" data-mp="2183z3wtngamf">
        <iframe src={GOOGLE_FORM_CANDIDAT} width="100%" height="2532" frameBorder={0} marginHeight={0} marginWidth={0} title="Formulaire de candidature — Zillenium Alternance" className="w-full rounded-2xl" data-id="element-560" data-mp="257w8ucus4mtd">
          Chargement…
        </iframe>
      </div>
    </div>;
}

/* -------------------------------------------------------------------------- */
/* Formulaire natif, prêt pour l'API PHP                                      */
/* -------------------------------------------------------------------------- */

function FormulaireNatif() {
  const ouvertureForm = useMemo(() => Date.now(), []);
  const fileRef = useRef<HTMLInputElement>(null);
  const [cv, setCv] = useState<File | null>(null);
  const [erreurs, setErreurs] = useState<Erreurs>({});
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [erreurGlobale, setErreurGlobale] = useState<string | null>(null);
  const offresOuvertes = OFFRES.filter(o => o.ouverte);
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setErreurs(prev => ({
      ...prev,
      cv: ''
    }));
    if (!file) {
      setCv(null);
      return;
    }
    if (file.size > CV_MAX_SIZE) {
      setErreurs(prev => ({
        ...prev,
        cv: 'Votre CV dépasse 5 Mo. Merci de le compresser ou de l’enregistrer en PDF.'
      }));
      setCv(null);
      return;
    }
    if (!CV_ACCEPTED_TYPES.includes(file.type)) {
      setErreurs(prev => ({
        ...prev,
        cv: 'Format non accepté. Utilisez un fichier PDF, DOC ou DOCX.'
      }));
      setCv(null);
      return;
    }
    setCv(file);
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErreurGlobale(null);
    if (!cv) {
      setErreurs(prev => ({
        ...prev,
        cv: 'Merci de joindre votre CV.'
      }));
      return;
    }
    const form = e.currentTarget;
    const data = new FormData(form);
    data.set('cv', cv);
    data.set('ouvertureForm', String(ouvertureForm));
    setEnvoiEnCours(true);
    try {
      // Jeton anti-falsification fourni par l'API
      const jetonReponse = await fetch(CANDIDATURE_API_URL, {
        credentials: 'include'
      });
      const {
        csrf
      } = (await jetonReponse.json()) as {
        csrf?: string;
      };
      if (csrf) data.set('csrf', csrf);
      const reponse = await fetch(CANDIDATURE_API_URL, {
        method: 'POST',
        body: data,
        credentials: 'include'
      });
      const resultat = (await reponse.json()) as CandidatureResponse;
      if (resultat.ok) {
        setReference(resultat.reference ?? null);
        setErreurs({});
        form.reset();
        setCv(null);
      } else {
        setErreurs(resultat.champs ?? {});
        setErreurGlobale(resultat.message ?? 'Votre candidature n’a pas pu être enregistrée. Merci de réessayer.');
      }
    } catch {
      setErreurGlobale('La connexion au serveur a échoué. Vérifiez votre connexion internet ou appelez-nous directement.');
    } finally {
      setEnvoiEnCours(false);
    }
  };

  /* Confirmation affichée directement sur le site */
  if (reference) {
    return <div role="status" className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-12 text-center" data-id="element-561" data-mp="2dbsoamcu0wl2">
        <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-562" data-mp="1klj1zobcqko4">
          <CheckCircle2 className="w-8 h-8 text-green-700" data-id="element-563" data-mp="1pkvkxxz3r7lf" />
        </div>
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark mb-4" data-id="element-564" data-mp="1tlpd53jkv8k4">
          Candidature enregistrée
        </h2>
        <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto mb-2" data-id="element-565" data-mp="1ym3iuexpi6ez">
          Merci ! Votre candidature et votre CV nous sont bien parvenus. Vous
          allez recevoir un accusé de réception par e-mail, et notre équipe vous
          contacte sous 48 heures ouvrées.
        </p>
        <p className="font-bold text-primary-dark mb-8" data-id="element-566" data-mp="19vv0qf2xm8g0">
          Votre référence : {reference}
        </p>
        <Button variant="outline" className="border-primary/20 text-primary-dark hover:bg-primary/5" onClick={() => setReference(null)} data-id="element-567" data-mp="15vkxha6ixw4u">
          Déposer une autre candidature
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} encType="multipart/form-data" noValidate className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-10" data-id="element-568" data-mp="r66jfos06qj4">
      <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark mb-3" data-id="element-569" data-mp="10x9ot2rytro7">
        Déposer votre candidature
      </h2>
      <p className="text-muted-foreground leading-relaxed mb-8" data-id="element-570" data-mp="w4gkgg0eslcn">
        Renseignez vos coordonnées, la formation souhaitée et joignez votre CV.
        Notre équipe vous contacte sous 48 heures ouvrées.
      </p>

      {/* Champ piège invisible — les robots le remplissent, les humains non */}
      <div className="hidden" aria-hidden="true" data-id="element-571" data-mp="2f362r5eb87pq">
        <label htmlFor="piege" data-id="element-572" data-mp="2jv5xmjfiwafg">Ne pas remplir</label>
        <input id="piege" name="piege" type="text" tabIndex={-1} data-id="element-573" data-mp="2jx0lo6m3cinr" />
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mb-6" data-id="element-574" data-mp="2652j6emvxl0k">
        <Champ id="prenom" label="Prénom" required placeholder="Camille" erreur={erreurs.prenom} data-id="element-575" data-mp="2a5mdlag2qaj0" />
        <Champ id="nom" label="Nom" required placeholder="Durand" erreur={erreurs.nom} data-id="element-576" data-mp="2xvqgtgpco89h" />
        <Champ id="email" label="Adresse e-mail" type="email" required placeholder="camille.durand@email.fr" erreur={erreurs.email} data-id="element-577" data-mp="2ousldmsz992i" />
        <Champ id="telephone" label="Téléphone" type="tel" required placeholder="06 12 34 56 78" erreur={erreurs.telephone} data-id="element-578" data-mp="1dfn3hbk9z3yb" />
        <Champ id="ville" label="Ville" required placeholder="Joigny" erreur={erreurs.ville} data-id="element-579" data-mp="294jqwwi72rzq" />
        <div className="space-y-2" data-id="element-580" data-mp="251dfzbk50cmx">
          <Label htmlFor="formation" data-id="element-581" data-mp="203u1cajj1xug">Formation souhaitée *</Label>
          <select id="formation" name="formation" required className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" data-id="element-582" data-mp="204dhpmtj4e6d">
            <option value="" data-id="element-583" data-mp="2j2ptlgbts25n">Sélectionnez une formation</option>
            {FORMATIONS.map(f => <option key={f.id} value={f.title} data-id="element-584" data-mp="2e3mro4r56c1w">
                {f.title}
              </option>)}
            <option value="Je ne sais pas encore" data-id="element-585" data-mp="3bjipv6cfj4xd">Je ne sais pas encore</option>
          </select>
          {erreurs.formation && <Erreur texte={erreurs.formation} data-id="element-586" data-mp="36r931z6m2kzb" />}
        </div>
      </div>

      <div className="space-y-2 mb-6" data-id="element-587" data-mp="3e3ot1bt7yrcv">
        <Label htmlFor="offre" data-id="element-588" data-mp="3e4qdu801es7s">Offre d'alternance qui vous intéresse</Label>
        <select id="offre" name="offre" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" data-id="element-589" data-mp="3j1qc3wqnaqoc">
          <option value="" data-id="element-590" data-mp="xoph1k0y3fo">Candidature spontanée</option>
          {offresOuvertes.map(o => <option key={o.id} value={o.poste} data-id="element-591" data-mp="5v82xz8wec6k">
              {o.poste}
              {o.ville ? ` — ${o.ville}` : ''}
            </option>)}
        </select>
      </div>

      <div className="space-y-2 mb-6" data-id="element-592" data-mp="fljjqmdokfuv">
        <Label htmlFor="message" data-id="element-593" data-mp="fkhvthbkfyvq">Message de motivation *</Label>
        <Textarea id="message" name="message" required rows={5} minLength={20} maxLength={3000} placeholder="Présentez-vous en quelques lignes : votre parcours, votre projet et ce qui vous motive." data-id="element-594" data-mp="kl5wyvtidw3o" />
        {erreurs.message && <Erreur texte={erreurs.message} data-id="element-595" data-mp="1fsc22i68mzpk" />}
      </div>

      {/* Dépôt du CV */}
      <div className="mb-6" data-id="element-596" data-mp="1fq97dku54iei">
        <Label htmlFor="cv" data-id="element-597" data-mp="1kr6odu3ez6ef">Votre CV *</Label>
        <label htmlFor="cv" className="mt-2 flex items-center gap-4 border-2 border-dashed border-primary/20 rounded-2xl p-5 cursor-pointer hover:border-primary/40 hover:bg-lavande/40 transition-colors" data-id="element-598" data-mp="1kqdnh9r4zb47">
          <span className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center shrink-0" data-id="element-599" data-mp="1yitibn9vw2u1">
            <Paperclip className="w-5 h-5 text-primary" data-id="element-600" data-mp="23ifxi299vt5h" />
          </span>
          <span className="min-w-0 flex-1" data-id="element-601" data-mp="23gd73h47jy08">
            <span className="block font-semibold text-primary-dark truncate" data-id="element-602" data-mp="28hj2xnt601dz">
              {cv ? cv.name : 'Sélectionner votre CV'}
            </span>
            <span className="block text-sm text-muted-foreground" data-id="element-603" data-mp="96hxmqwus9m9">
              PDF, DOC ou DOCX — 5 Mo maximum
            </span>
          </span>
          {cv && <button type="button" aria-label="Retirer le CV" onClick={e => {
          e.preventDefault();
          setCv(null);
          if (fileRef.current) fileRef.current.value = '';
        }} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors" data-id="element-604" data-mp="6la65fa1e473">
              <X className="w-4 h-4 text-primary-dark" data-id="element-605" data-mp="pgigyka5zcjx" />
            </button>}
        </label>
        <input ref={fileRef} id="cv" name="cv" type="file" accept=".pdf,.doc,.docx" className="sr-only" onChange={handleFile} data-id="element-606" data-mp="khf8sqyw0q7q" />
        {erreurs.cv && <Erreur texte={erreurs.cv} data-id="element-607" data-mp="30amnkhf7h1uf" />}
      </div>

      {/* Consentement RGPD */}
      <div className="flex items-start gap-3 mb-8" data-id="element-608" data-mp="308jpda3wg2em">
        <input id="consentement" name="consentement" type="checkbox" value="1" required className="mt-1 w-4 h-4 shrink-0 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring" data-id="element-609" data-mp="2vai3yij03qzz" />
        <label htmlFor="consentement" className="text-sm text-muted-foreground leading-relaxed" data-id="element-610" data-mp="1de6loxkcbb1q">
          J'accepte que mes données et mon CV soient utilisés par Zillenium
          Alternance dans le cadre du traitement de ma candidature, et conservés
          pendant 2 ans maximum. Voir la{' '}
          <Link to="/confidentialite" className="text-primary font-semibold underline underline-offset-4 hover:text-brand-orange" data-id="element-611" data-mp="18fvbglq4uyiz">
            politique de confidentialité
          </Link>
          . *
        </label>
      </div>

      {erreurGlobale && <div role="alert" className="flex items-start gap-3 bg-red-50 text-red-900 rounded-2xl p-5 mb-6" data-id="element-612" data-mp="kqah76oj4eue">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-613" data-mp="pnkd956fbor7" />
          <p className="text-sm leading-relaxed" data-id="element-614" data-mp="61sejzivhzkr">{erreurGlobale}</p>
        </div>}

      <Button type="submit" size="lg" disabled={envoiEnCours} className="w-full bg-brand-orange hover:bg-[#E67E22] text-white text-lg py-7 group disabled:opacity-70" data-id="element-615" data-mp="21gvusa2m9bx">
        {envoiEnCours ? <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-616" data-mp="b3q235yb3gji" />
            Envoi en cours…
          </> : <>
            Envoyer ma candidature
            <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-617" data-mp="kyrt67c56soq" />
          </>}
      </Button>

      <p className="text-xs text-muted-foreground mt-5 leading-relaxed" data-id="element-618" data-mp="yqg0lxbg4szd">
        Champs marqués d'un astérisque obligatoires. Votre CV est transmis de
        manière chiffrée et stocké dans un espace non accessible publiquement.
      </p>
    </form>;
}

/* -------------------------------------------------------------------------- */
/* Petits composants internes                                                 */
/* -------------------------------------------------------------------------- */

function Champ({
  id,
  label,
  required,
  type = 'text',
  placeholder,
  erreur
}: {
  id: string;
  label: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  erreur?: string;
}) {
  return <div className="space-y-2" data-id="element-619" data-mp="3bzs4e6jel6rz">
      <Label htmlFor={id} data-id="element-620" data-mp="2eap0p4zimjpl">
        {label} {required && '*'}
      </Label>
      <Input id={id} name={id} type={type} required={required} placeholder={placeholder} aria-invalid={Boolean(erreur)} data-id="element-621" data-mp="2j9a2v9ybvdgo" />
      {erreur && <Erreur texte={erreur} data-id="element-622" data-mp="2oafut5k5ywp5" />}
    </div>;
}
function Erreur({
  texte
}: {
  texte: string;
}) {
  return <p className="flex items-center gap-2 text-sm text-red-700" data-id="element-623" data-mp="2t53f0s84x4xx">
      <AlertCircle className="w-4 h-4 shrink-0" data-id="element-624" data-mp="3hm6bfn87mye" />
      {texte}
    </p>;
}